import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  Typography,
  Skeleton,
  useTheme,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StatusChip } from '@components/StatusChip';
import { formatDueDate } from '@pages/Work/WorkOrderListPage/utils/dateUtils';
import { useSidebarWorkOrders, type StatusFilter } from '../hooks';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'Incomplete', label: 'Incomplete' },
  { value: 'Complete', label: 'Complete' },
  { value: 'Invoiced', label: 'Invoiced' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All' }
];

type WorkOrdersListProps = {
  currentJobNumber?: string;
  collapsed?: boolean;
  onNavigate: (jobNumber: string) => void;
};

export const WorkOrdersList: React.FC<WorkOrdersListProps> = ({
  currentJobNumber,
  collapsed = false,
  onNavigate
}) => {
  const theme = useTheme();
  const { t } = useTranslation('sidebar');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('Incomplete');
  const { workOrders, isLoading, error } = useSidebarWorkOrders({
    statusFilter
  });

  const handleFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value as StatusFilter);
  };

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="error">
          {t('workOrders.loadError')}
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <List sx={{ py: 0 }}>
        {[1, 2, 3].map(i => (
          <ListItem key={i} disablePadding>
            <Box sx={{ width: '100%', p: 1.5 }}>
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </Box>
          </ListItem>
        ))}
      </List>
    );
  }

  if (collapsed) {
    return null;
  }

  const filterDropdown = (
    <Box sx={{ px: 2, py: 1 }}>
      <FormControl fullWidth size="small">
        <Select
          value={statusFilter}
          onChange={handleFilterChange}
          sx={{
            fontSize: '0.75rem',
            '& .MuiSelect-select': {
              py: 1,
              display: 'flex',
              alignItems: 'center'
            }
          }}
        >
          {STATUS_OPTIONS.map(option => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{ fontSize: '0.75rem' }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  if (workOrders.length === 0) {
    return (
      <Box>
        {filterDropdown}
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {t('workOrders.noWorkOrders')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {filterDropdown}
      <List sx={{ py: 0, maxHeight: '35vh', overflow: 'auto' }}>
        {workOrders.map(wo => {
          const isActive = currentJobNumber === wo.jobNumber;
          const dueDateText = formatDueDate(wo.dueDate);

          const dueDateColor = wo.isOverdue
            ? 'error.main'
            : wo.isDueToday
              ? 'warning.main'
              : 'text.secondary';

          return (
            <ListItem key={wo.jobNumber} disablePadding>
              <ListItemButton
                onClick={() => onNavigate(wo.jobNumber)}
                selected={isActive}
                sx={{
                  px: 2,
                  py: 1.5,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  backgroundColor: isActive
                    ? theme.palette.mode === 'light'
                      ? 'rgba(1, 103, 124, 0.08)'
                      : 'rgba(68, 156, 180, 0.16)'
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: isActive
                      ? theme.palette.mode === 'light'
                        ? 'rgba(1, 103, 124, 0.12)'
                        : 'rgba(68, 156, 180, 0.24)'
                      : theme.palette.action.hover
                  },
                  borderLeft: isActive
                    ? `3px solid ${theme.palette.primary.main}`
                    : 'none'
                }}
              >
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      mb: 0.5
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isActive ? 600 : 500,
                        color: theme.palette.text.primary
                      }}
                    >
                      {wo.jobNumber}
                    </Typography>
                    <StatusChip
                      label={wo.status || 'Unknown'}
                      size="small"
                      sx={{ height: 20 }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                      mb: 0.25
                    }}
                  >
                    {wo.customerName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: dueDateColor,
                      fontWeight: wo.isOverdue || wo.isDueToday ? 600 : 400
                    }}
                  >
                    {t('workOrders.dueLabel')} {dueDateText}
                  </Typography>
                </>
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};
